import {FC, useCallback} from 'react'

import {yupResolver} from '@hookform/resolvers/yup'
import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import Image from 'next/legacy/image'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {FormProvider, useForm} from 'react-hook-form'
import * as yup from 'yup'

import FormFooter from 'components/blocks/FormFooter'
import FormHeader from 'components/blocks/FormHeader'
import Button from 'components/elements/Button'
import FieldInput from 'components/elements/fields/FieldInput'
import {posthogTrack, klaviyoTrack, pushGTMLayer, posthogIdentify} from 'src/utils'
import {useAuthStore, useDraftStore} from 'src/utils/contexts/RootStore.context'
import useAsyncFlow from 'src/utils/hooks/useAsyncFlow'
import {messages} from 'src/utils/validations'

import s from './SignUpForm.module.scss'

const cx = cn.bind(s)

const schema = yup
	.object({
		firstName: yup.string().trim().required(messages.required),
		lastName: yup.string().trim().required(messages.required),
		email: yup.string().email('please enter a valid email').required(messages.required),
	})
	.required()

interface ISignUpFormProps {
	className?: string
}

const SignUpForm: FC<ISignUpFormProps> = ({className}) => {
	const router = useRouter()
	const authStore = useAuthStore()
	const draftStore = useDraftStore()
	const [signUpFlow, {isLoading, errorMessage}] = useAsyncFlow<typeof authStore.signUp>(
		authStore.signUp,
	)

	const methods = useForm<yup.TypeOf<typeof schema>>({
		resolver: yupResolver(schema),
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
		},
	})

	const onSubmit = useCallback(
		async (data: yup.TypeOf<typeof schema>) => {
			const {email, firstName, lastName} = data
			posthogTrack('[SIGN UP] Sign up button clicked')
			if (!email || !firstName || !lastName) return

			const {draftId, type, template, theme} = draftStore

			const response = await signUpFlow({
				firstName,
				lastName,
				email,
				draftId,
				type,
			})({
				onSuccess: () => {
					posthogIdentify(email, `${firstName} ${lastName}`)
					posthogTrack('[SIGN UP] User successfully registered', {email})
					klaviyoTrack('Signed up', {})
					pushGTMLayer({
						event: 'user_registered',
						email,
					})
				},
			})

			if (response?.data.ok) {
				if (response.data.data.draftId && type) {
					draftStore.setDraft(response.data.data.draftId, type, template, theme)
				}
				return router.push(`/pricing`)
			}
		},
		[draftStore, router, signUpFlow],
	)

	return (
		<div className={cx('wrapper', className)}>
			<FormProvider {...methods}>
				<form className={s.form} data-testid="sign-up-form">
					<FormHeader
						className={cx('header')}
						title=""
						message={errorMessage || methods.formState.errors}
					/>

					<FieldInput
						wrapperClassName={cx('input-spaces')}
						name="firstName"
						type="text"
						label="First Name"
						placeholder="Write first name"
					/>
					<FieldInput
						wrapperClassName={cx('input-spaces')}
						name="lastName"
						type="text"
						label="Last Name"
						placeholder="Write last name"
					/>
					<FieldInput
						wrapperClassName={cx('input-spaces')}
						name="email"
						type="email"
						label="Email"
						placeholder="Write email address"
					/>

					<Button
						className={cx('btn')}
						size="big"
						isLoading={isLoading}
						onClick={methods.handleSubmit(onSubmit)}
						disabled={methods.formState.isSubmitted && !methods.formState.isValid}
						data-testid="sign-up-btn">
						Sign Up
					</Button>

					<div className={s.terms}>
						<div className={cx('terms__icon-wrapper')}>
							<Image
								className={cx('terms__icon')}
								alt="Spam Icon"
								layout="fixed"
								src="/icons/icon-spam.png"
								height="34"
								width="34"
							/>
						</div>
						<div className="px-2 fs-xxs">
							<p className="terms-of-service-text">
								Your e-mail will not be shared with anyone. We will not send you junk mail or
								newsletters.
							</p>
							<p className="terms-of-service-text">
								By continuing, you confirm that you have read and consent to our
								<Link href="/terms" aria-label="To the terms page">
									Terms of Use
								</Link>{' '}
								and{' '}
								<Link href="/privacy-policy" aria-label="To the privacy policy page">
									Privacy Policy
								</Link>
							</p>
						</div>
					</div>
					<FormFooter type="sign-in" className={cx('footer')} />
				</form>
			</FormProvider>
		</div>
	)
}

SignUpForm.displayName = 'SignUpForm'

export default observer(SignUpForm)
